<template>
    <v-dialog :value="value" width="720" persistent scrollable>
        <v-card>
            <v-card-title class="text-h6">
                {{ $t('unit.mode.title') }}
                <span class="text-body-2 grey--text ml-3">
                    {{ step === 'group' ? $t('unit.mode.step_group') : $t('unit.mode.step_mode') }}
                </span>
            </v-card-title>
            <v-divider></v-divider>

            <v-card-text class="pt-4">
                <!-- 第一層：大類（規格 §3.2）。刻意分兩層而不是攤平七個選項——
                     七張卡片並排會讓「純寫日記」和「找不同」看起來像同一個層級的東西，
                     而它們對老師的意義差很多。 -->
                <template v-if="step === 'group'">
                    <v-row>
                        <v-col v-for="g in groups" :key="g.key" cols="12" sm="4">
                            <v-card outlined class="qm-card pa-4" @click="chooseGroup(g)">
                                <div class="text-subtitle-1 font-weight-medium">
                                    {{ $t(`unit.mode.group_${g.key}`) }}
                                </div>
                                <div class="text-caption grey--text text--darken-1 mt-1">
                                    {{ $t(`unit.mode.group_${g.key}_note`) }}
                                </div>
                                <div class="text-caption grey--text mt-2">
                                    {{ $t('unit.mode.group_count', { n: g.modes.length }) }}
                                </div>
                            </v-card>
                        </v-col>
                    </v-row>
                </template>

                <!-- 第二層：細項。⚠ **不可逆警告一定要在這一層**（規格 §3.2）——
                     老師是在這裡做出不可逆的決定的，警告放在第一層他還沒選定就看到，
                     等真的要按下去時已經滑過去了。 -->
                <template v-else>
                    <v-row>
                        <v-col v-for="m in currentModes" :key="m" cols="12" sm="6">
                            <v-card outlined class="qm-card qm-card--mode pa-4"
                                :class="{ 'qm-card--active': selectedMode === m }"
                                @click="selectedMode = m">
                                <div class="text-subtitle-1 font-weight-medium">
                                    {{ $t(`unit.mode.mode_${m}`) }}
                                </div>
                                <div class="text-caption grey--text text--darken-1 mt-1">
                                    {{ $t(`unit.mode.mode_${m}_note`) }}
                                </div>
                            </v-card>
                        </v-col>
                    </v-row>

                    <div class="text-caption orange--text text--darken-3 mt-3">
                        {{ $t('unit.mode.irreversible') }}
                    </div>
                </template>
            </v-card-text>

            <v-divider></v-divider>
            <!-- 「取消」在最左、「返回」貼著「建立」（2026-09-10 依需求方指示交換）。
                 ⚠ 只有「返回」與「建立」有 `v-if="step === 'mode'"`——第一步（選分類）
                 時那兩顆都不出現，只剩「取消」。交換之後第一步的版面是左邊一顆取消，
                 與交換前（左邊空、右邊一顆取消）不同，這是預期的。 -->
            <v-card-actions>
                <v-btn text class="normal-case" :disabled="isBusy" @click="close">
                    {{ $t('button.cancel') }}
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn v-if="step === 'mode'" text class="normal-case"
                    :disabled="isBusy" @click="backToGroup">
                    {{ $t('button.back') }}
                </v-btn>
                <v-btn v-if="step === 'mode'" color="primary" depressed class="normal-case"
                    :disabled="!selectedMode || isBusy" :loading="isBusy" @click="confirm">
                    {{ $t('unit.mode.create') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
/// ⚠ 分類定義在 questModeUtils，這裡不要另存一份——同一份資料兩處遲早不同步
import { MODE_GROUPS } from './questModeUtils';

/**
 * 題目模式的兩層選擇 — 見 mangachat-studio-api docs/plans/MediaLibrary.spec.md §3.2、§5.1。
 *
 * ⚠ **模式建立後不可變更**（ADR 0002）。所以第二層一定要有不可逆警告，
 * 而且這個對話框**只負責選**，建立由呼叫端做——「新增關卡」與「關卡內新增題目」
 * 是兩支不同的端點，但兩者都要帶 `mode`（規格 §5.1）。只接前者會建出沒有模式的題目。
 *
 * ⚠ **這個對話框在修一個 B7 造成的破壞**，不只是新功能：B7 讓 `mode` 變成必填，
 * 而在它上線之前 `UnitList.vue` 與 `UnitEdit.vue` 都送空 body，兩個按鈕會回 400。
 *
 * ⚠ 大類與細項的文案在規格 §11 是「未定」。這裡先寫一版可用的，定案後只改 i18n。
 */

export default {
    name: 'QuestModeDialog',
    props: {
        /** 對話框開關（v-model）。 */
        value: { type: Boolean, default: false },
        /**
         * 呼叫端正在建立中。⚠ 由**呼叫端**持有而不是這裡自己管：
         * 建立的 API 是呼叫端打的（addUnit 或 addQuest），成功後才關對話框。
         * 失敗時對話框要留著讓老師重試，而不是關掉、讓他重選一次模式。
         */
        busy: { type: Boolean, default: false },
    },
    data() {
        return {
            groups: MODE_GROUPS,
            step: 'group',
            groupKey: null,
            selectedMode: null,
        };
    },
    computed: {
        isBusy() {
            return this.busy;
        },
        currentModes() {
            const found = MODE_GROUPS.find((g) => g.key === this.groupKey);
            return found ? found.modes : [];
        },
    },
    watch: {
        /// 每次開啟都回到第一層——沿用上一次的選擇會讓老師以為自己沒選就建出東西
        value(open) {
            if (open) {
                this.step = 'group';
                this.groupKey = null;
                this.selectedMode = null;
            }
        },
    },
    methods: {
        chooseGroup(group) {
            this.groupKey = group.key;
            /// ⚠ 大類只有一個細項時**仍然進第二層**（目前只有「日記」是這種情況）。
            /// 直接建立會跳過不可逆警告，而那是規格 §3.2 明文要求要看到的。
            /// 順手預選，老師只要確認。
            this.selectedMode = group.modes.length === 1 ? group.modes[0] : null;
            this.step = 'mode';
        },

        backToGroup() {
            this.step = 'group';
            this.selectedMode = null;
        },

        /**
         * ⚠ 只 emit，**不關對話框**。關閉由呼叫端在建立成功後做——
         * 建立失敗時對話框要留著，否則老師得從第一層重選一次。
         */
        confirm() {
            if (!this.selectedMode || this.isBusy) {
                return;
            }
            this.$emit('confirm', this.selectedMode);
        },

        close() {
            if (this.isBusy) {
                return;
            }
            this.$emit('input', false);
        },
    },
};
</script>

<style scoped>
.qm-card {
    cursor: pointer;
    height: 100%;
    transition: border-color 0.15s, box-shadow 0.15s;
}

.qm-card:hover {
    border-color: var(--v-primary-base, #1976D2);
}

.qm-card--active {
    border-color: var(--v-primary-base, #1976D2);
    box-shadow: 0 0 0 2px var(--v-primary-base, #1976D2) inset;
}
</style>
